import React, { type CSSProperties, useMemo } from 'react';
import { z } from 'zod';

import { getLuminance } from '@mui/material/styles';
import { Button as BaseEmailButton } from '@react-email/button';
import { useGlobalLinkColor } from '@wp-block-builder/stores/WaypointDocumentStore/useGlobalStyles';
import { useRenderLiquidTemplate } from '@wp-block-builder/stores/WaypointRendererStore';

import defineBlock from './helpers/defineBlock';

export const ButtonBlockSchema = defineBlock(
  'Button',
  z.object({
    buttonBackgroundColor: z.string().nullish(),
    buttonStyle: z.string().nullish(),
    buttonTextColor: z.string().nullish(),
    fullWidth: z.boolean().nullish(),
    size: z.string().nullish(),
    text: z.string().nullish(),
    url: z.string().nullish(),
  })
);
export type TButtonBlock = z.infer<typeof ButtonBlockSchema>;

function getRoundedCorners(value: unknown) {
  switch (value) {
    case 'rectangle':
      return '0';
    case 'pill':
      return '64px';
    case 'rounded':
    default:
      return '4px';
  }
}

function getButtonSizePadding(value: unknown) {
  switch (value) {
    case 'x-small':
      return '4px 8px';
    case 'small':
      return '8px 12px';
    case 'large':
      return '16px 32px';
    case 'medium':
    default:
      return '12px 20px';
  }
}

function getTextContrastColor(bgColor: string) {
  return getLuminance(bgColor) > 0.5 ? '#000000' : '#FFFFFF';
}

function useButtonStyle(block: TButtonBlock) {
  const linkColor = useGlobalLinkColor();
  return useMemo<CSSProperties>(() => {
    const backgroundColor = block.props?.buttonBackgroundColor ?? linkColor;
    const fullWidth = block.props?.fullWidth;
    return {
      backgroundColor,
      color: block.props?.buttonTextColor ?? getTextContrastColor(backgroundColor),
      padding: getButtonSizePadding(block.props?.size),
      borderRadius: getRoundedCorners(block.props?.buttonStyle),
      width: fullWidth ? '100%' : 'auto',
      display: fullWidth ? 'block' : 'inline-block',
    };
  }, [block, linkColor]);
}

type ButtonBlockProps = {
  block: TButtonBlock;
  skipLiquid: boolean;
};
export default function ButtonBlock({ block, skipLiquid }: ButtonBlockProps) {
  const href = useRenderLiquidTemplate({ template: block.props?.url ?? '', skipLiquid });
  const children = useRenderLiquidTemplate({ template: block.props?.text ?? '', skipLiquid });
  const buttonStyle = useButtonStyle(block);

  return (
    <BaseEmailButton href={href} style={buttonStyle}>
      {children}
    </BaseEmailButton>
  );
}
